import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Check, X } from 'lucide-react';
import { format } from 'date-fns';
import { formatCurrency } from '@/lib/currencies';

type Transaction = {
  id: string;
  user_id: string;
  type: string;
  amount: number;
  status: string;
  admin_note: string | null;
  created_at: string;
  currency: string;
  profiles: {
    email: string | null;
    phone: string | null;
    balance: number;
    currency: string;
  };
};

export default function TransactionManagement() {
  const { t, language } = useLanguage();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTx, setSelectedTx] = useState<Transaction | null>(null);
  const [adminNote, setAdminNote] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*, profiles(email, phone, balance, currency)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTransactions(data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (
    txId: string,
    userId: string,
    status: 'approved' | 'rejected',
    type: string,
    amount: number,
    currentBalance: number,
    currency: string
  ) => {
    try {
      const { error: txError } = await supabase
        .from('transactions')
        .update({
          status,
          admin_note: adminNote || null,
        })
        .eq('id', txId);

      if (txError) throw txError;

      if (status === 'approved') {
        let newBalance = currentBalance;
        if (type === 'deposit') {
          newBalance += amount;
        } else if (type === 'withdrawal') {
          newBalance -= amount;
        }

        const { error: balanceError } = await supabase
          .from('profiles')
          .update({ balance: newBalance })
          .eq('id', userId);

        if (balanceError) throw balanceError;
      }

      toast.success(`Transaction ${status}!`);
      setDialogOpen(false);
      setAdminNote('');
      fetchTransactions();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
      case 'completed':
        return 'bg-green-500';
      case 'rejected':
        return 'bg-red-500';
      case 'pending':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('allTransactions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {transactions.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1 flex-1">
                  <div className="font-medium">
                    {tx.profiles?.email || tx.profiles?.phone}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {tx.type.replace('_', ' ').toUpperCase()} - {formatCurrency(tx.amount, tx.currency || 'USD', language)}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {format(new Date(tx.created_at), 'PPp')}
                  </div>
                  <Badge className={getStatusColor(tx.status)}>
                    {t(tx.status)}
                  </Badge>
                  {tx.admin_note && (
                    <p className="text-sm text-muted-foreground italic">{tx.admin_note}</p>
                  )}
                </div>
                {tx.status === 'pending' && tx.type !== 'admin_adjustment' && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-green-600"
                      onClick={() => {
                        setSelectedTx(tx);
                        setDialogOpen(true);
                      }}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      {t('approve')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600"
                      onClick={() => {
                        setSelectedTx(tx);
                        setDialogOpen(true);
                      }}
                    >
                      <X className="h-4 w-4 mr-2" />
                      {t('reject')}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Transaction</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Admin Note (Optional)</Label>
              <Textarea
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Add a note for the user..."
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() =>
                  selectedTx &&
                  handleUpdateStatus(
                    selectedTx.id,
                    selectedTx.user_id,
                    'approved',
                    selectedTx.type,
                    selectedTx.amount,
                    selectedTx.profiles.balance,
                    selectedTx.currency
                  )
                }
                className="flex-1"
              >
                <Check className="h-4 w-4 mr-2" />
                {t('approve')}
              </Button>
              <Button
                onClick={() =>
                  selectedTx &&
                  handleUpdateStatus(
                    selectedTx.id,
                    selectedTx.user_id,
                    'rejected',
                    selectedTx.type,
                    selectedTx.amount,
                    selectedTx.profiles.balance,
                    selectedTx.currency
                  )
                }
                variant="destructive"
                className="flex-1"
              >
                <X className="h-4 w-4 mr-2" />
                {t('reject')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}